import { Button } from "@/modern/components/ui/button";
import { UserAvatar } from "@/modern/components/user-avatar";
import { Pencil } from "lucide-react";
import { cn } from "@/modern/utils";
import { NOTIFY_TYPE_COLUMN_CONTENT } from "../constants/ownershipConstants";
import { UserSelectorDialog } from "./UserSelectorDialog";
import { OverflowUsersPopover } from "./OverflowUsersPopover";
import type { BoardUser } from "@/react/hooks/entityHooks/useEntityOwnership";

interface NotificationTypeRowProps {
  notifyType: string;
  entityName: string;
  boardUserIds: number[];
  boardUsers: Array<BoardUser>;
  onSelectionChange: (notifyType: string, userIds: number[]) => void;
}

export const NotificationTypeRow = ({
  notifyType,
  entityName,
  boardUserIds,
  boardUsers,
  onSelectionChange
}: NotificationTypeRowProps) => {
  const config = NOTIFY_TYPE_COLUMN_CONTENT[notifyType as keyof typeof NOTIFY_TYPE_COLUMN_CONTENT];
  if (!config) return null;

  const Icon = config.icon;
  const assignedUsers = boardUserIds.map((id) => boardUsers.find((u) => u.id === id)).filter(Boolean) as Array<BoardUser>;

  return (
    <div className="py-3 px-2 transition group">
      {/* Top: Icon + Title & Description */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 pt-0.5">
          <Icon className={cn("h-5 w-5", config.color)} />
        </div>
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <div className="font-medium text-sm text-foreground leading-none">{config.title}</div>
          <div className="text-xs text-muted-foreground leading-tight">{config.description(entityName)}</div>
        </div>
      </div>

      {/* Bottom: Assigned label + Avatars + Pencil */}
      <div className="flex items-center justify-between ml-8">
        <div className="flex items-center gap-3">
          {assignedUsers.length > 0 ? (
            <>
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Assigned</span>
              <div className="flex items-center -space-x-2">
                {/* Show first 3 users with overlapping */}
                {assignedUsers.slice(0, 3).map((user) => (
                  <UserAvatar key={user.id} user={user} size="large" showTooltip={true} showRemove={false} />
                ))}
                {/* Show +N badge if more than 3 users */}
                {assignedUsers.length > 3 && <OverflowUsersPopover assignedUsers={assignedUsers.slice(3)} />}
              </div>
            </>
          ) : (
            <span className="text-xs text-muted-foreground italic">No users assigned</span>
          )}
        </div>

        <UserSelectorDialog notifyType={notifyType} selectedUserIds={boardUserIds} boardUsers={boardUsers} onSelectionChange={onSelectionChange}>
          <Button variant="ghost" size="icon-lg">
            <Pencil />
          </Button>
        </UserSelectorDialog>
      </div>
    </div>
  );
};